import React, { useState, useRef, useEffect } from 'react';
import { businessCalendar } from '../../services/businessCalendar';
import './BusinessDayDatePicker.css';

interface BusinessDayDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  className?: string;
  disabled?: boolean;
  error?: boolean;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

const toYMD = (d: Date): string =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const formatDisplay = (d: Date): string =>
  `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

/** Parse DD/MM/YYYY string into a Date, or null if invalid */
const parseDDMMYYYY = (text: string): Date | null => {
  const m = text.trim().match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10) - 1;
  const year = parseInt(m[3], 10);
  const d = new Date(year, month, day);
  // Verify the date didn't roll (e.g. 31/02 → 03/03)
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
  return d;
};

const DAY_LABELS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const BusinessDayDatePicker: React.FC<BusinessDayDatePickerProps> = ({
  value,
  onChange,
  className = '',
  disabled = false,
  error = false,
}) => {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => value || new Date());
  const [inputText, setInputText] = useState(() => value ? formatDisplay(value) : '');
  const [inputError, setInputError] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync inputText and viewDate when value changes externally
  useEffect(() => {
    setInputText(value ? formatDisplay(value) : '');
    setInputError(false);
    if (value) setViewDate(value);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  let startOffset = firstDay.getDay() - 1; // Monday=0
  if (startOffset < 0) startOffset = 6;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const selectedYMD = value ? toYMD(value) : '';
  const todayYMD = toYMD(new Date());

  const handleSelect = (day: number) => {
    const selected = new Date(year, month, day);
    onChange(selected);
    setOpen(false);
  };

  const handleInputBlur = () => {
    const trimmed = inputText.trim();
    // Empty input — clear the value
    if (!trimmed) {
      setInputError(false);
      onChange(null);
      return;
    }
    const parsed = parseDDMMYYYY(trimmed);
    if (!parsed) {
      // Invalid format — revert
      setInputError(true);
      return;
    }
    if (!businessCalendar.isBusinessDay(parsed)) {
      // Non-business day — revert
      setInputError(true);
      return;
    }
    // Valid business day — accept
    setInputError(false);
    onChange(parsed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === 'Escape') {
      // Revert to current value
      setInputText(value ? formatDisplay(value) : '');
      setInputError(false);
      setOpen(false);
    }
  };

  const handleInputClick = () => {
    if (!disabled) setOpen(!open);
  };

  const handleCalendarIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setOpen(!open);
      inputRef.current?.focus();
    }
  };

  return (
    <div className={`bddp-wrapper ${className}`} ref={wrapperRef}>
      <div className="bddp-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className={`form-input bddp-display ${(inputError || error) ? 'bddp-error' : ''} ${className}`}
          value={inputText}
          placeholder="DD/MM/YYYY"
          disabled={disabled}
          onChange={(e) => {
            setInputText(e.target.value);
            setInputError(false);
          }}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          onClick={handleInputClick}
        />
        <button
          type="button"
          className="bddp-calendar-btn"
          onClick={handleCalendarIconClick}
          disabled={disabled}
          tabIndex={-1}
        >
          &#128197;
        </button>
      </div>
      {open && (
        <div className="bddp-dropdown">
          <div className="bddp-nav">
            <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))}>&lsaquo;</button>
            <span>{MONTH_NAMES[month]} {year}</span>
            <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))}>&rsaquo;</button>
          </div>
          <div className="bddp-grid">
            {DAY_LABELS.map(d => (
              <div key={d} className="bddp-dayhead">{d}</div>
            ))}
            {cells.map((day, i) => {
              if (day === null) return <div key={`e${i}`} className="bddp-cell empty" />;
              const dateStr = `${year}-${pad2(month + 1)}-${pad2(day)}`;
              const cellDate = new Date(year, month, day);
              const isNonBusiness = !businessCalendar.isBusinessDay(cellDate);
              const isSelected = dateStr === selectedYMD;
              const isToday = dateStr === todayYMD;
              const holiday = businessCalendar.getHoliday(cellDate);
              return (
                <button
                  key={dateStr}
                  type="button"
                  className={
                    'bddp-cell' +
                    (isSelected ? ' selected' : '') +
                    (isNonBusiness ? ' disabled-day' : '') +
                    (isToday ? ' today' : '')
                  }
                  onClick={() => handleSelect(day)}
                  disabled={isNonBusiness}
                  title={holiday ? holiday.name : undefined}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDayDatePicker;
